import { z } from 'zod';

/**
 * Orcamento dos eventos (docs/eventos, decidido pelo GrowuP a 20-10-2026), so
 * no back-office, com `events.budget` (Gestor de Eventos, Administrador geral):
 *
 *   - `/api/site/events/:id/budget/...`     orcamento, custos, receitas, faturas
 *   - `/api/site/event-budget/...`          categorias, contas por fechar, CSV
 *
 * Valores em centimos, com IVA. As faturas sao de fornecedores: documentos que
 * a associacao recebe, guardados como vieram e nunca publicos.
 */

export const financeKindSchema = z.enum(['cost', 'revenue']);
export type FinanceKind = z.infer<typeof financeKindSchema>;

/** `planned` = Previsto/Prevista; `settled` = Pago/Recebida. */
export const financeStatusSchema = z.enum(['planned', 'settled']);
export type FinanceStatus = z.infer<typeof financeStatusSchema>;

/** Tipos aceites nas faturas e recibos, confirmados pelos bytes do ficheiro. */
export const FINANCE_DOCUMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export type FinanceDocumentType = (typeof FINANCE_DOCUMENT_TYPES)[number];
export const FINANCE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'aaaa-mm-dd');
/** Ate 10 milhoes de euros: chega e evita numeros que o Postgres nao guarda. */
const centsSchema = z.number().int().min(0).max(1_000_000_000);

export const budgetCategorySchema = z.object({
  id: z.string().uuid(),
  kind: financeKindSchema,
  name: z.string(),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type BudgetCategory = z.infer<typeof budgetCategorySchema>;

export const budgetCategoryInputSchema = z.object({
  kind: financeKindSchema,
  name: z.string().trim().min(1).max(60),
  active: z.boolean().default(true),
});
export type BudgetCategoryInput = z.infer<typeof budgetCategoryInputSchema>;

export const financeDocumentSchema = z.object({
  id: z.string().uuid(),
  originalName: z.string(),
  contentType: z.enum(FINANCE_DOCUMENT_TYPES),
  bytes: z.number().int(),
  uploadedAt: z.string(),
  uploadedBy: z.string().nullable(),
});
export type FinanceDocument = z.infer<typeof financeDocumentSchema>;

export const financeEntrySchema = z.object({
  id: z.string().uuid(),
  kind: financeKindSchema,
  categoryId: z.string().uuid(),
  categoryName: z.string(),
  description: z.string(),
  counterparty: z.string(),
  entryDate: dateSchema.nullable(),
  amountCents: z.number().int(),
  vatCents: z.number().int().nullable(),
  status: financeStatusSchema,
  notes: z.string(),
  documents: z.array(financeDocumentSchema),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});
export type FinanceEntry = z.infer<typeof financeEntrySchema>;

export const financeEntryInputSchema = z
  .object({
    kind: financeKindSchema,
    categoryId: z.string().uuid(),
    description: z.string().trim().min(1).max(140),
    counterparty: z.string().trim().max(120).default(''),
    entryDate: dateSchema.nullable().default(null),
    amountCents: centsSchema.refine((n) => n > 0, 'o valor tem de ser maior que zero'),
    vatCents: centsSchema.nullable().default(null),
    status: financeStatusSchema.default('planned'),
    notes: z.string().trim().max(2000).default(''),
  })
  .refine((e) => e.vatCents === null || e.vatCents <= e.amountCents, {
    message: 'o IVA nao pode ser maior que o valor',
    path: ['vatCents'],
  });
export type FinanceEntryInput = z.input<typeof financeEntryInputSchema>;

/** Gasto/recebido e previsto de uma categoria, e o orcamentado (so custos). */
export const budgetCategoryTotalsSchema = z.object({
  categoryId: z.string().uuid(),
  kind: financeKindSchema,
  name: z.string(),
  budgetCents: z.number().int().nullable(),
  settledCents: z.number().int(),
  plannedCents: z.number().int(),
});
export type BudgetCategoryTotals = z.infer<typeof budgetCategoryTotalsSchema>;

export const eventBudgetSchema = z.object({
  eventId: z.string().uuid(),
  /** `null` = sem orcamento definido. */
  budgetCents: z.number().int().nullable(),
  accountsClosedAt: z.string().nullable(),
  accountsClosedBy: z.string().nullable(),
  totals: z.object({
    costSettledCents: z.number().int(),
    costPlannedCents: z.number().int(),
    /** Orcamento menos pago e previsto; `null` sem orcamento. */
    availableCents: z.number().int().nullable(),
    revenueSettledCents: z.number().int(),
    revenuePlannedCents: z.number().int(),
    /** Recebido menos pago. */
    balanceCents: z.number().int(),
    /** Custos pagos sem nenhuma fatura ou recibo. */
    missingDocuments: z.number().int(),
  }),
  categories: z.array(budgetCategoryTotalsSchema),
  entries: z.array(financeEntrySchema),
});
export type EventBudget = z.infer<typeof eventBudgetSchema>;

export const eventBudgetInputSchema = z.object({
  budgetCents: centsSchema.nullable(),
  /** Valor orcamentado por categoria de custos; as que faltam ficam sem valor. */
  lines: z
    .array(z.object({ categoryId: z.string().uuid(), amountCents: centsSchema }))
    .max(100)
    .default([]),
});
export type EventBudgetInput = z.input<typeof eventBudgetInputSchema>;

/** Um evento ja passado cujas contas nao estao fechadas. */
export const openAccountsEventSchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  endsAt: z.string(),
  costPlannedCents: z.number().int(),
  revenuePlannedCents: z.number().int(),
  missingDocuments: z.number().int(),
});
export type OpenAccountsEvent = z.infer<typeof openAccountsEventSchema>;

export const FINANCE_STATUS_LABELS: Record<FinanceKind, Record<FinanceStatus, string>> = {
  cost: { planned: 'Previsto', settled: 'Pago' },
  revenue: { planned: 'Prevista', settled: 'Recebida' },
};

/** Nome de um ficheiro no ZIP para o contabilista: `aaaa-mm-dd_fornecedor_valor.ext`. */
export function financeDocumentFileName(
  entry: {
    entryDate: string | null;
    counterparty: string;
    description: string;
    amountCents: number;
  },
  contentType: FinanceDocumentType,
): string {
  const ext =
    contentType === 'application/pdf' ? 'pdf' : contentType === 'image/png' ? 'png' : 'jpg';
  const who = (entry.counterparty || entry.description)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .toLowerCase();
  const value = (entry.amountCents / 100).toFixed(2).replace('.', ',');
  return `${entry.entryDate ?? 'sem-data'}_${who || 'documento'}_${value}.${ext}`;
}
